import { Injectable } from '@angular/core';
import { DateAdapter } from '@angular/material/core';
import { BehaviorSubject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { LOCALIZATION_LOCALE, Language } from '../../models';
import { BaseComponent } from '../../core';
import { AbstractStorageService } from '../storage/abstract-storage.service';

@Injectable()
export class AbstractI18nService extends BaseComponent {
  private selectedLanguage$$ = new BehaviorSubject<Language>(null);
  public readonly selectedLanguage$ = this.selectedLanguage$$.asObservable();

  constructor(
    protected translateService: TranslateService,
    protected storageService: AbstractStorageService,
    protected matDateAdapter: DateAdapter<any>
  ) {
    super();
  }

  public async init() {
    // Warning: this subscription will always be alive for the app's lifetime
    this.translateService.onLangChange
      .pipe(takeUntil(this.componentDestroyed))
      .subscribe((event: LangChangeEvent) => {
        this.storageService.saveSelectedLanguage(
          event.lang as LOCALIZATION_LOCALE
        );
      });

    const storedLanguage = this.getStoredLanguage();
    const browserLanguage = this.getBrowserLanguage();

    this.setLanguage(
      storedLanguage || browserLanguage || this.getSupportedLanguage()?.[0]
    );
  }

  /**
   * Sets the current language.
   * Note: The current language is saved to the local storage.
   * If no parameter is specified, the language is loaded from local storage (if present).
   * @param language The IETF language code to set.
   */
  public setLanguage(language: Language) {
    this.translateService.use(language.languageCode);
    this.selectedLanguage$$.next(language);
    this.matDateAdapter.setLocale(language.languageCode);
  }

  /**
   * Gets the current language.
   * @return The current language code.
   */
  public getSelectedLanguage(): Language {
    return this.generateLanguageFromString(this.translateService.currentLang);
  }

  public getSupportedLanguage(): Language[] {
    return [];
  }

  private getStoredLanguage(): Language {
    return this.generateLanguageFromString(
      this.storageService.getSelectedLanguage()
    );
  }

  private getBrowserLanguage(): Language {
    const language = this.translateService.getBrowserCultureLang();

    if (!language) {
      return null;
    }

    const matchedLanguage = this.generateLanguageFromString(language);

    if (matchedLanguage) {
      return matchedLanguage;
    }

    return this.getSupportedLanguage().find((l) =>
      l.languageCode.startsWith(language.split('-')[0])
    );
  }

  private generateLanguageFromString(lang: string): Language {
    return this.getSupportedLanguage().find((l) => l.languageCode === lang);
  }
}
